/* eslint-disable @typescript-eslint/naming-convention */
import { Maybe } from "shared/types/Maybe"
import { Primitive } from "shared/types/Primitive"
import { ProductConfigurationFormData } from "shared/types/ProductConfiguration"
import { RJSFFormData, RJSFSchema } from "shared/types/RJSF"

export const flatten = (
  data: object,
  flattenPaths = true,
  prefix = "",
  delimitier = "."
) => {
  const result: { [key: string]: string | number | null } = {}

  if (data === null || data === undefined) {
    return data
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value === null) {
      result[key] = value
    } else if (typeof value === "object") {
      Object.assign(
        result,
        flatten(
          value,
          flattenPaths,
          prefix ? `${prefix}${delimitier}${key}` : key,
          delimitier
        )
      )
    } else if (flattenPaths && prefix) {
      result[`${prefix}${delimitier}${key}`] = value
    } else {
      result[key] = value
    }
  })

  return result
}
export function flattenFormData(formData: Maybe<Primitive>): Maybe<Primitive>
export function flattenFormData(formData: RJSFFormData): RJSFFormData
export function flattenFormData(
  formData: RJSFFormData | Maybe<Primitive>
): RJSFFormData | Maybe<Primitive>
export function flattenFormData(formData: RJSFFormData | Maybe<Primitive>) {
  if (formData && typeof formData === "object") {
    return flatten(formData, false, "", "_") as RJSFFormData
  }
  return formData as Maybe<Primitive>
}

export function flattenProductConfigurationFormData(
  formData: ProductConfigurationFormData,
  parentKey?: string
) {
  let result: RJSFFormData = {}
  if (parentKey) {
    result[parentKey] = formData.value
  }
  if (formData.product) {
    result = {
      ...result,
      ...flattenFormData(formData.product)
    }
  }
  if (formData.addons) {
    result = {
      ...result,
      ...flattenFormData(
        Object.fromEntries(
          Object.entries(formData.addons).map(([key, addon]) => [
            key,
            flattenProductConfigurationFormData(addon, key)
          ])
        )
      )
    }
  }

  return result
}

const flattenSchema_Internal = ({
  data,
  remove,
  exclude
}: {
  data: { [x: string]: RJSFSchema }
  remove?: (schema: RJSFSchema, id: string) => boolean
  exclude?: (id: string) => boolean
}) => {
  const entriesResult: [string, RJSFSchema][] = []

  Object.entries(data).forEach(([key, value]: [string, RJSFSchema]) => {
    if (!remove || !remove(value, key)) {
      if (value.type === "object" && value.properties) {
        if (exclude && exclude(key)) {
          entriesResult.push([key, { fieldID: key, ...value }])
        } else {
          entriesResult.push(
            ...flattenSchema_Internal({
              data: value.properties as { [x: string]: RJSFSchema },
              remove,
              exclude
            })
          )
        }
      } else {
        entriesResult.push([key, { fieldID: key, ...value }])
      }
    }
  })
  return entriesResult
}

export const flattenSchema = ({
  data,
  remove,
  exclude
}: {
  data: RJSFSchema
  remove?: (schema: RJSFSchema, id: string) => boolean
  exclude?: (id: string) => boolean
}): RJSFSchema => ({
  ...data,
  properties: Object.fromEntries(
    flattenSchema_Internal({
      data: data.properties as { [x: string]: RJSFSchema },
      remove,
      exclude
    })
  )
})
